// Split out of InstanceDetailPage.tsx, then migrated onto components/ui.
//
// The output pane is deliberately dark — every `ink-*` here is CLAUDE.md's
// escape hatch, not a stray `gray-*` — so the `<pre>` itself stays
// hand-written: `Card` is `bg-white` and there is no ink variant of it (nor of
// `Button` / `Input`). The white panel *around* the pane is an ordinary Card;
// the pane inside it is untouched. See the report for the missing-primitive
// note.

import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useScriptHistory, useScriptRun, useRunScript } from '../../hooks/useScripts'
import type { Instance, ScriptRun } from '../../api/client'
import { isBusy } from '../../lib/instance-status'
import { extractApiError } from './shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  LinkButton,
  Textarea,
} from '../../components/ui'
import type { BadgeTone } from '../../components/ui'

export const MAX_SCRIPT_BYTES = 1048576

// The five run states were yellow / blue / green / red / orange. The palette
// contract has six roles and no orange, so `pending` and `timeout` both land on
// warning — the label inside the pill is what tells them apart.
const STATUS_TONE: Record<ScriptRun['status'], BadgeTone> = {
  pending: 'warning',
  running: 'accent',
  success: 'success',
  failed: 'danger',
  timeout: 'warning',
}

// ── ConsoleTab ──────────────────────────────────────────────────────────────

export function ConsoleTab({ instance }: { instance: Instance }) {
  const { t } = useTranslation()
  const [script, setScript] = useState('')
  const [backupFirst, setBackupFirst] = useState(true)
  const [showConfirm, setShowConfirm] = useState(false)
  const [activeRunId, setActiveRunId] = useState<string | null>(null)
  const [expandedRun, setExpandedRun] = useState<string | null>(null)
  const [showHistory, setShowHistory] = useState(false)
  const [runError, setRunError] = useState('')
  const outputRef = useRef<HTMLPreElement>(null)
  const busy = isBusy(instance.status)

  const runMutation = useRunScript(instance.id)
  const { data: history = [] } = useScriptHistory(instance.id)
  const { data: activeRun } = useScriptRun(instance.id, activeRunId ?? '', !!activeRunId)

  // Auto-scroll output.
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight
    }
  }, [activeRun?.output])

  const isRunning = activeRun?.status === 'pending' || activeRun?.status === 'running'
  const tooLarge = script.length > MAX_SCRIPT_BYTES

  // `mutate` with callbacks rather than an awaited `mutateAsync`: nothing here
  // needs the promise, and a rejection that escapes an event handler is both a
  // silent failure for the user and an unhandled rejection in the test run.
  const handleRun = () => {
    setRunError('')
    runMutation.mutate(
      { script, backupFirst },
      {
        onSuccess: (run) => {
          setActiveRunId(run.id)
          setShowConfirm(false)
        },
        onError: (e) => {
          setRunError(extractApiError(e) ?? t('detail.consoleExtra.runError'))
          setShowConfirm(false)
        },
      },
    )
  }

  return (
    <div className="space-y-4">
      {/* Disclaimer */}
      <Alert tone="warning" className="flex gap-3">
        <svg className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <div>
          <h4 className="font-semibold">{t('detail.console.disclaimer')}</h4>
          <p className="text-xs mt-1">{t('detail.console.disclaimerDetail')}</p>
        </div>
      </Alert>

      {/* Script Editor */}
      <Card>
        <Textarea
          value={script}
          onChange={(e) => setScript(e.target.value)}
          placeholder={t('detail.console.placeholder')}
          invalid={tooLarge}
          className="font-mono resize-y"
          style={{ minHeight: 200 }}
          disabled={isRunning || busy}
        />
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-4">
            <Checkbox
              label={t('detail.console.backupCheckbox')}
              checked={backupFirst}
              onChange={(e) => setBackupFirst(e.target.checked)}
              disabled={isRunning || busy}
            />
            <span className="text-xs text-gray-400">
              {(script.length / 1024).toFixed(0)} KB / 1 MB
            </span>
          </div>
          <Button
            onClick={() => setShowConfirm(true)}
            disabled={!script.trim() || tooLarge || busy || instance.status !== 'running'}
            loading={isRunning || runMutation.isPending}
          >
            {isRunning ? t('detail.console.running') : t('detail.console.runButton')}
          </Button>
        </div>

        {tooLarge && (
          <p className="text-xs text-red-600 mt-2">{t('detail.console.errorTooLarge')}</p>
        )}
        {instance.status !== 'running' && (
          <p className="text-xs text-gray-400 mt-2">{t('detail.console.errorNotRunning')}</p>
        )}
      </Card>

      {/* Confirmation — the script runs against the live database, so the gate
          stays exactly as explicit as it was, now in the app's dialog shell. */}
      {showConfirm && (
        <ConfirmDialog
          title={t('detail.console.confirmTitle')}
          message={t('detail.console.confirmMessage')}
          confirmLabel={t('detail.console.confirmRun')}
          cancelLabel={t('detail.console.confirmCancel')}
          tone="primary"
          busy={runMutation.isPending}
          onConfirm={handleRun}
          onCancel={() => setShowConfirm(false)}
        />
      )}

      {/* Active Run Output */}
      {activeRun && (
        <Card>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-800">{t('detail.console.outputHeading')}</h4>
            <div className="flex items-center gap-2">
              {isRunning && (
                <div className="flex items-center gap-1.5 text-brand-600 text-xs">
                  <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {t('detail.console.running')}
                </div>
              )}
              <Badge tone={STATUS_TONE[activeRun.status] ?? 'neutral'}>{activeRun.status}</Badge>
              {activeRun.exit_code != null && (
                <span className="text-xs text-gray-500">
                  {t('detail.console.exitCode')}: {activeRun.exit_code}
                </span>
              )}
              {activeRun.duration_ms != null && (
                <span className="text-xs text-gray-500">
                  {t('detail.console.duration')}: {(activeRun.duration_ms / 1000).toFixed(1)}s
                </span>
              )}
            </div>
          </div>
          <pre
            ref={outputRef}
            className="bg-ink-900 text-green-400 text-xs font-mono p-4 rounded-lg overflow-auto whitespace-pre-wrap"
            style={{ maxHeight: 400 }}
          >
            {activeRun.output || (isRunning ? '...' : t('detail.console.noOutput'))}
          </pre>
        </Card>
      )}

      {/* Mutation error */}
      {runError && <Alert tone="danger">{runError}</Alert>}

      {/* History */}
      {history.length > 0 && (
        <Card>
          <button
            type="button"
            onClick={() => setShowHistory(!showHistory)}
            aria-expanded={showHistory}
            className="flex items-center justify-between w-full"
          >
            <h4 className="font-semibold text-gray-800">{t('detail.console.historyHeading')}</h4>
            <svg className={`w-4 h-4 text-gray-400 transition-transform ${showHistory ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showHistory && (
            <div className="mt-3 space-y-2">
              {history.map((run) => (
                <div key={run.id} className="border border-gray-100 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge tone={STATUS_TONE[run.status] ?? 'neutral'}>{run.status}</Badge>
                      <span className="text-xs text-gray-500">
                        {new Date(run.created_at).toLocaleString()}
                      </span>
                      {run.duration_ms != null && (
                        <span className="text-xs text-gray-400">
                          {(run.duration_ms / 1000).toFixed(1)}s
                        </span>
                      )}
                    </div>
                    <LinkButton
                      tone="accent"
                      onClick={() => {
                        setExpandedRun(expandedRun === run.id ? null : run.id)
                        setActiveRunId(run.id)
                      }}
                    >
                      {expandedRun === run.id ? t('detail.console.hideOutput') : t('detail.console.viewOutput')}
                    </LinkButton>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  )
}
